import { riskGapConfidenceRatings, riskGapStatuses } from '@ctw/shared/api/fhir/models/risk-gap';
import { z } from 'zod';

export const cardTypes = [
  'conditions',
  'referrals',
  'medications',
  'diagnostics',
  'episodesOfCare',
  'vitals',
  'careGaps',
  'trends',
  'zusInformation',
  'risk-gaps',
] as const;

export type CardType = (typeof cardTypes)[number];

const cardSchema = z.object({
  type: z.enum(cardTypes),
});

const riskGapSubstatusOptionSchema = z.object({
  label: z.string(),
  value: z.string(),
});

const riskGapsSchema = z.object({
  lensEnabled: z.boolean().optional().default(false),
  lensCurrentYears: z.array(z.number()).optional(),
  lensIncludeAllPatients: z.boolean().optional().default(false),
  showConfidenceLevels: z.boolean().optional(),
  displayGapsWithConfidenceLevelsAbove: z
    .enum(riskGapConfidenceRatings as [string, ...Array<string>])
    .optional(),
  disableExpectedToCloseStatus: z.boolean().optional(),
  uiExcludedHccCodes: z.array(z.string()).optional(),
  statusOptions: z
    .partialRecord(
      z.enum(riskGapStatuses),
      z.object({
        substatusOptions: z.array(riskGapSubstatusOptionSchema).optional(),
      }),
    )
    .optional(),
});

export const builderConfigSchema = z.strictObject({
  builderId: z.string(),
  showRequestRecords: z.boolean().optional(),
  patientOverviewPage: z
    .object({
      cards: z.array(cardSchema),
    })
    .optional(),
  systemMRN: z.string().optional(),
  // nested riskGapsSchema is not strict, instead it strips unrecognized keys.
  riskGaps: riskGapsSchema.optional(),
  hospitalizationExtensionFilters: z
    .object({
      systemUrl: z.string(),
      displayName: z.string(),
    })
    .optional(),
});

export type CardConfig = z.infer<typeof cardSchema>;
export type RiskGapSubstatusOption = z.infer<typeof riskGapSubstatusOptionSchema>;
export type RiskGapsConfig = z.infer<typeof riskGapsSchema>;
export type BuilderConfig = z.infer<typeof builderConfigSchema>;

export function getDefaultBuilderConfig(builderId: string): BuilderConfig {
  return {
    builderId,
    showRequestRecords: true,
    patientOverviewPage: {
      cards: [
        { type: 'conditions' },
        { type: 'referrals' },
        { type: 'medications' },
        { type: 'diagnostics' },
        { type: 'episodesOfCare' },
        { type: 'vitals' },
        { type: 'zusInformation' },
        { type: 'careGaps' },
        { type: 'risk-gaps' },
      ],
    },
    riskGaps: {
      lensEnabled: false,
      showConfidenceLevels: true,
      lensIncludeAllPatients: false,
      statusOptions: {},
    },
  };
}
